import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  addDays,
  findConflicts,
  formatAddressShort,
  formatDayLabel,
  startOfDay,
  startOfWeek,
  toLocalDateKey,
  weekDays,
  type Job,
} from '@fsm/shared';
import { keys, useJobs, useReschedule, useTechs } from '../lib/queries';
import { Board } from '../components/calendar/Board';
import { JobsMap } from '../components/JobsMap';
import { useBoardDrag } from '../components/calendar/useBoardDrag';
import type { BoardColumn, BoardJob, DropTarget } from '../components/calendar/types';
import { Badge, Button, FilterTabs, Spinner, Toast, cx } from '../components/ui';
import { NewJobModal } from '../components/NewJobModal';

type View = 'day' | 'week' | 'map';

/** How long a job dragged out of the backlog is assumed to take. */
const DEFAULT_JOB_MINUTES = 120;

export function DispatchPage() {
  const navigate = useNavigate();
  const [view, setView] = useState<View>('day');
  const [anchor, setAnchor] = useState(() => startOfDay(new Date()));
  const [techFilter, setTechFilter] = useState<string>('all');
  const [toast, setToast] = useState<{ message: string; tone: 'error' | 'info' } | null>(null);
  const [creating, setCreating] = useState(false);

  const { data: techs = [] } = useTechs();

  const range = useMemo(() => {
    /* The map shows one day, like the day board: a week of pins on one screen
       is a cloud of dots nobody can plan a route from. */
    if (view === 'day' || view === 'map') {
      return { from: startOfDay(anchor), to: addDays(startOfDay(anchor), 1) };
    }
    const start = startOfWeek(anchor);
    return { from: start, to: addDays(start, 7) };
  }, [view, anchor]);

  const listQuery = useMemo(
    () => ({ from: range.from.toISOString(), to: range.to.toISOString(), limit: 200 }),
    [range],
  );
  const listKey = keys.jobs(listQuery);

  const { data: scheduled = [], isLoading } = useJobs(listQuery);
  const { data: backlog = [] } = useJobs({ unscheduledOnly: true, limit: 50 });

  const reschedule = useReschedule((message) => setToast({ message, tone: 'error' }));

  // --- columns --------------------------------------------------------------

  const visibleTechs = techFilter === 'all' ? techs : techs.filter((t) => t.id === techFilter);

  const columns: BoardColumn[] = useMemo(() => {
    if (view === 'day') {
      return visibleTechs.map((tech) => ({
        key: `tech:${tech.id}`,
        day: startOfDay(anchor),
        techId: tech.id,
        label: tech.name,
        sublabel: tech.phone ?? undefined,
        color: tech.colorHex,
      }));
    }
    // Week view: a column per day. With one tech selected the column carries
    // their id, so a drop also assigns; with "all technicians" the drop keeps
    // whichever tech the job already had.
    const only = techFilter === 'all' ? null : techFilter;
    return weekDays(anchor).map((day) => ({
      key: `day:${toLocalDateKey(day)}`,
      day,
      techId: only,
      label: formatDayLabel(day).split(',')[0] ?? '',
      sublabel: new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric' }).format(day),
      muted: day.getDay() === 0,
    }));
  }, [view, visibleTechs, anchor, techFilter]);

  const columnKeyFor = useCallback(
    (job: Job): string | null => {
      if (view === 'day') return job.assignedTechId ? `tech:${job.assignedTechId}` : null;
      if (!job.scheduledStart) return null;
      if (techFilter !== 'all' && job.assignedTechId !== techFilter) return null;
      return `day:${toLocalDateKey(new Date(job.scheduledStart))}`;
    },
    [view, techFilter],
  );

  // --- drag -----------------------------------------------------------------

  const hasConflict = useCallback(
    (job: BoardJob, target: DropTarget) => {
      const techId = target.column.techId ?? job.assignedTechId;
      if (!techId) return false;
      return (
        findConflicts(
          { techId, start: target.start, end: target.end, excludeJobId: job.id },
          scheduled.map((j) => ({
            id: j.id,
            assignedTechId: j.assignedTechId,
            scheduledStart: j.scheduledStart,
            scheduledEnd: j.scheduledEnd,
            status: j.status,
          })),
        ).length > 0
      );
    },
    [scheduled],
  );

  const onDrop = useCallback(
    (job: BoardJob, target: DropTarget) => {
      const techId = target.column.techId ?? job.assignedTechId;
      if (!techId) {
        setToast({
          message: 'Pick a single technician before dropping into the week view',
          tone: 'info',
        });
        return;
      }
      reschedule.mutate({
        jobId: job.id,
        listKey,
        input: {
          assignedTechId: techId,
          scheduledStart: target.start.toISOString(),
          scheduledEnd: target.end.toISOString(),
          allowConflict: false,
          expectedVersion: job.version,
        },
      });
    },
    [reschedule, listKey],
  );

  // Owned here rather than inside <Board> so the backlog rail, which lives
  // outside the board, can start a drag with the same session.
  const drag = useBoardDrag({
    columns,
    onDrop,
    onClick: (job) => navigate(`/jobs/${job.id}`),
    hasConflict,
  });

  // --- toolbar --------------------------------------------------------------

  const step = view === 'day' ? 1 : 7;
  const title =
    view === 'day'
      ? new Intl.DateTimeFormat('en-US', {
          weekday: 'long',
          month: 'long',
          day: 'numeric',
        }).format(anchor)
      : `${formatDayLabel(startOfWeek(anchor))} – ${formatDayLabel(addDays(startOfWeek(anchor), 6))}`;

  const isToday = toLocalDateKey(anchor) === toLocalDateKey(new Date());

  return (
    <>
      <header className="flex shrink-0 items-center gap-3 border-b border-line bg-surface px-[30px] py-3.5">
        <div className="flex items-center rounded-[7px] border border-line-2">
          <button
            className="grid h-8 w-8 place-items-center rounded-l-[6px] text-ink-2 hover:bg-raised"
            onClick={() => setAnchor((d) => addDays(d, -step))}
            aria-label="Previous"
          >
            ‹
          </button>
          <button
            className={cx(
              'h-8 border-x border-line-2 px-3 text-[12.5px] font-semibold hover:bg-raised',
              isToday ? 'text-accent' : 'text-ink-2',
            )}
            onClick={() => setAnchor(startOfDay(new Date()))}
          >
            Today
          </button>
          <button
            className="grid h-8 w-8 place-items-center rounded-r-[6px] text-ink-2 hover:bg-raised"
            onClick={() => setAnchor((d) => addDays(d, step))}
            aria-label="Next"
          >
            ›
          </button>
        </div>

        <h1 className="font-display text-[17px] font-extrabold tracking-[-0.025em]">{title}</h1>
        {isLoading && <Spinner className="size-4 text-ink-4" />}

        <div className="ml-auto flex items-center gap-2">
          <TechFilter techs={techs} value={techFilter} onChange={setTechFilter} />

          <FilterTabs
            value={view}
            onChange={setView}
            options={[
              { value: 'day' as const, label: 'Day' },
              { value: 'week' as const, label: 'Week' },
              { value: 'map' as const, label: 'Map' },
            ]}
          />

          <Button variant="primary" size="sm" icon="plus" onClick={() => setCreating(true)}>
            New job
          </Button>
        </div>
      </header>

      <div className="flex min-h-0 flex-1">
        {/* The backlog is a drag source for the board. There is nothing to
            drag a job onto on a map, so it would just be taking up room. */}
        {view !== 'map' && (
          <Backlog jobs={backlog} handlers={drag.handlers} draggingId={drag.drag?.jobId ?? null} />
        )}

        {view === 'map' ? (
          <JobsMap
            /*
             * The technician filter applies here too. It did not, at first,
             * and the result was a map that ignored the control sitting
             * directly above it — every pin shown whoever you picked.
             */
            jobs={
              techFilter === 'all'
                ? scheduled
                : scheduled.filter((job) => job.assignedTechId === techFilter)
            }
            techs={visibleTechs}
            onOpen={(job) => navigate(`/jobs/${job.id}`)}
          />
        ) : columns.length === 0 ? (
          <div className="grid flex-1 place-items-center text-[13px] text-ink-3">
            No technicians to show.
          </div>
        ) : (
          <Board
            columns={columns}
            jobs={scheduled}
            columnKeyFor={columnKeyFor}
            drag={drag}
            emptyMessage="Nothing scheduled in this window. Drag a job in from the backlog."
          />
        )}
      </div>

      {toast && <Toast message={toast.message} tone={toast.tone} onDismiss={() => setToast(null)} />}

      <NewJobModal
        open={creating}
        onClose={() => setCreating(false)}
        defaultDay={anchor}
        onCreated={(job) => {
          setCreating(false);
          navigate(`/jobs/${job.id}`);
        }}
      />
    </>
  );
}

/**
 * Pick a technician by typing their name.
 *
 * A plain select is fine at five technicians and useless at fifteen: you scan
 * a list looking for "Marisol" instead of typing "mar". This keeps the select's
 * behaviour — one value, always showing which — and adds the only thing a
 * growing crew needs, which is search.
 */
function TechFilter({
  techs,
  value,
  onChange,
}: {
  techs: Array<{ id: string; name: string; colorHex?: string | null }>;
  value: string;
  onChange: (value: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const box = useRef<HTMLDivElement>(null);

  // Clicking anywhere else closes it. Without this the panel survives a click
  // on a job block and hangs over the board.
  useEffect(() => {
    if (!open) return;
    const onDocumentPointerDown = (event: PointerEvent) => {
      if (!box.current?.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener('pointerdown', onDocumentPointerDown);
    return () => document.removeEventListener('pointerdown', onDocumentPointerDown);
  }, [open]);

  const selected = techs.find((t) => t.id === value);
  const label = value === 'all' ? 'All technicians' : (selected?.name ?? 'All technicians');

  const needle = query.trim().toLowerCase();
  const matches = needle ? techs.filter((t) => t.name.toLowerCase().includes(needle)) : techs;

  const choose = (next: string) => {
    onChange(next);
    setOpen(false);
    setQuery('');
  };

  return (
    <div ref={box} className="relative">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className={cx(
          'flex h-8 min-w-[168px] items-center gap-2 rounded-lg border border-line-2 bg-surface px-2.5 text-[13px]',
          'hover:border-ink-4',
        )}
      >
        {selected?.colorHex && (
          <span
            className="size-2 shrink-0 rounded-full"
            style={{ background: selected.colorHex }}
            aria-hidden
          />
        )}
        <span className="min-w-0 flex-1 truncate text-left">{label}</span>
        <span className="shrink-0 text-ink-4" aria-hidden>
          ▾
        </span>
      </button>

      {open && (
        <div className="absolute right-0 z-50 mt-1 w-[248px] overflow-hidden rounded-lg border border-line bg-surface shadow-[0_8px_24px_-6px_rgb(11_14_20/0.18),0_0_0_1px_rgb(11_14_20/0.06)]">
          <div className="border-b border-line p-2">
            <input
              autoFocus
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Escape') setOpen(false);
                if (e.key === 'Enter' && matches[0]) choose(matches[0].id);
              }}
              placeholder="Search technicians…"
              className="h-8 w-full rounded-md border border-line-2 px-2.5 text-[13px] outline-none focus:border-accent"
            />
          </div>

          <div className="max-h-[280px] overflow-y-auto py-1">
            <button
              type="button"
              onClick={() => choose('all')}
              className={cx(
                'flex w-full items-center gap-2 px-3 py-1.5 text-left text-[13px] hover:bg-canvas',
                value === 'all' && 'font-semibold text-accent',
              )}
            >
              All technicians
            </button>

            {matches.map((tech) => (
              <button
                key={tech.id}
                type="button"
                onClick={() => choose(tech.id)}
                className={cx(
                  'flex w-full items-center gap-2 px-3 py-1.5 text-left text-[13px] hover:bg-canvas',
                  value === tech.id && 'font-semibold text-accent',
                )}
              >
                <span
                  className="size-2 shrink-0 rounded-full"
                  style={{ background: tech.colorHex ?? '#94a3b8' }}
                  aria-hidden
                />
                <span className="min-w-0 truncate">{tech.name}</span>
              </button>
            ))}

            {matches.length === 0 && (
              <p className="px-3 py-3 text-center text-[12.5px] text-ink-4">
                Nobody by that name.
              </p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * The unscheduled backlog.
 *
 * Jobs here have neither a tech nor a window. Dragging one onto a technician
 * column gives it both in a single gesture, which is the whole reason the rail
 * exists — otherwise scheduling means opening a form.
 */
function Backlog({
  jobs,
  handlers,
  draggingId,
}: {
  jobs: Job[];
  handlers: ReturnType<typeof useBoardDrag>['handlers'];
  draggingId: string | null;
}) {
  return (
    <aside className="flex w-[236px] shrink-0 flex-col border-r border-line bg-canvas">
      <header className="flex items-center justify-between px-3.5 py-2.5">
        <h2 className="text-[11px] font-semibold tracking-[0.07em] text-ink-4 uppercase">
          Unscheduled
        </h2>
        <span
          className={cx(
            'rounded-full px-1.5 py-0.5 text-[11px] leading-none font-semibold tnum',
            // Work waiting to be assigned is the one number on this screen
            // that should nag. Zero is good news and is styled as such.
            jobs.length === 0 ? 'bg-surface text-ink-4' : 'bg-warn-soft text-warn',
          )}
        >
          {jobs.length}
        </span>
      </header>

      <div className="min-h-0 flex-1 space-y-1.5 overflow-y-auto p-2.5">
        {jobs.length === 0 && (
          <p className="px-1 py-6 text-center text-[12.5px] text-ink-4">
            Nothing waiting. Good week.
          </p>
        )}
        {jobs.map((job) => (
          <BacklogCard
            key={job.id}
            job={job}
            handlers={handlers}
            dragging={draggingId === job.id}
          />
        ))}
      </div>

      <p className="px-3.5 py-2.5 text-[11px] leading-snug text-ink-4">
        Drag onto a technician to schedule.
      </p>
    </aside>
  );
}

function BacklogCard({
  job,
  handlers,
  dragging,
}: {
  job: Job;
  handlers: ReturnType<typeof useBoardDrag>['handlers'];
  dragging: boolean;
}) {
  // An unscheduled job has no window, so give the drag a nominal one. The
  // dispatcher adjusts the end on the job itself if two hours is wrong.
  const start = new Date();
  const boardJob: BoardJob = {
    ...job,
    start,
    end: new Date(start.getTime() + DEFAULT_JOB_MINUTES * 60_000),
  };

  return (
    <article
      onPointerDown={(e) => handlers.onPointerDown(e, boardJob)}
      onPointerMove={handlers.onPointerMove}
      onPointerUp={handlers.onPointerUp}
      onPointerCancel={handlers.onPointerCancel}
      className={cx(
        'no-select w-full cursor-grab rounded-[9px] bg-surface px-3 py-2.5 text-left',
        'shadow-[0_1px_1px_rgb(13_17_23/0.04),0_0_0_1px_rgb(13_17_23/0.06)]',
        'transition-shadow duration-150',
        'hover:shadow-[0_4px_12px_-2px_rgb(13_17_23/0.12),0_0_0_1px_rgb(31_86_201/0.35)]',
        dragging && 'dragging',
      )}
      title={`#${job.number} — ${job.title}`}
    >
      {/*
       * The title leads. The job number used to sit above it in its own row,
       * which gave the least useful thing on the card the most prominent
       * position; it now trails the title in the quietest weight available.
       */}
      <div className="flex items-start gap-1.5">
        {/*
         * High priority is a dot, not a chip. A chip is three words wide and
         * was truncating the title — which is the thing the dispatcher is
         * actually reading — to make room for a label they can infer from a
         * colour. Emergency keeps its chip: it is rare, and it should interrupt.
         */}
        {job.priority === 'high' && (
          <span
            className="mt-[5px] size-[6px] shrink-0 rounded-full bg-warn"
            title="High priority"
            aria-label="High priority"
          />
        )}
        <span className="min-w-0 flex-1 truncate text-[12.5px] leading-[1.3] font-semibold tracking-[-0.005em] text-ink">
          {job.title}
        </span>
        {job.priority === 'emergency' && <Badge tone="red">Emergency</Badge>}
      </div>
      <div className="mt-1 truncate text-[11.5px] leading-tight text-ink-2">
        {job.customer?.name}
      </div>
      <div className="mt-0.5 flex items-baseline gap-1.5 text-[11px] leading-tight text-ink-4">
        <span className="tnum">#{job.number}</span>
        <span aria-hidden>·</span>
        <span className="truncate">{formatAddressShort(job.property)}</span>
      </div>
    </article>
  );
}
